import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Pencil, Trash2, Users } from 'lucide-react';

export default function Departments() {
  const { user } = useAuth();
  const canManage = ['admin', 'hr'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/departments');
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await api.post('/departments', { name: name.trim() });
      toast.success(`Department "${name.trim()}" created`);
      setOpen(false); setName(''); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveRename = async () => {
    if (!editing?.name?.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      const { data } = await api.patch(`/departments/${editing.id}`, { name: editing.name.trim() });
      toast.success(`Renamed${data.updated_users ? ` — ${data.updated_users} employee record(s) updated` : ''}`);
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete department "${d.name}"?`)) return;
    try {
      await api.delete(`/departments/${d.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="departments-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Organisation</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Departments</h1>
          <p className="text-slate-500 mt-1">{items.length} department{items.length !== 1 ? 's' : ''} · Use these when creating new employees and assigning reporting managers.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-department-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Plus className="w-4 h-4 mr-2" /> New department
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md" data-testid="new-department-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">New department</DialogTitle>
                <p className="text-sm text-slate-500">Examples: Engineering · Product · Human Resources · Operations</p>
              </DialogHeader>
              <div className="mt-2">
                <Label className="text-xs font-semibold text-slate-600">Name *</Label>
                <Input data-testid="department-name-input" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" autoFocus />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-department" onClick={create} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Saving…' : 'Create department'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-400" data-testid="no-departments">
          <Building2 className="w-12 h-12 mx-auto opacity-30 mb-3" />
          {canManage ? 'No departments yet — create your first one to organise the team.' : 'No departments configured yet.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map(d => (
            <div key={d.id} data-testid={`department-${d.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-slate-900 truncate">{d.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                      <Users className="w-3 h-3" /> {d.headcount} employee{d.headcount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div className="flex gap-1">
                    <button data-testid={`edit-department-${d.id}`} onClick={() => setEditing({ id: d.id, name: d.name })} className="p-2 text-slate-400 hover:text-[#3D4A8E] rounded-lg hover:bg-slate-50" title="Rename">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-department-${d.id}`} onClick={() => remove(d)} className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-slate-50" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-3 pt-3 border-t border-slate-100">
                Created by {d.created_by_name || 'system'}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString()}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="rename-department-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Rename department</DialogTitle>
            <p className="text-sm text-slate-500">All employees currently in this department will be updated automatically.</p>
          </DialogHeader>
          <div className="mt-2">
            <Label className="text-xs font-semibold text-slate-600">New name</Label>
            <Input data-testid="rename-department-input" value={editing?.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-xl mt-1" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="submit-rename-department" onClick={saveRename} disabled={saving} className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
